import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Star, DollarSign, Calendar, TrendingUp, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const CustomersPanel = () => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCustomerDetails = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/email/${encodeURIComponent(email)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedCustomer(data.customer);
        setCustomerBookings(data.bookings);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      Bronze: 'bg-amber-700/20 text-amber-300 border-amber-700',
      Silver: 'bg-gray-400/20 text-gray-300 border-gray-400',
      Gold: 'bg-yellow-500/20 text-yellow-300 border-yellow-500',
      Platinum: 'bg-purple-500/20 text-purple-300 border-purple-400'
    };
    return colors[tier] || colors.Bronze;
  };

  const getTierDiscount = (tier) => {
    const discounts = {
      Bronze: '0%',
      Silver: '5%',
      Gold: '10%',
      Platinum: '15%'
    };
    return discounts[tier] || '0%';
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (selectedCustomer) {
    return (
      <div className="space-y-6">
        <Button 
          variant="outline" 
          onClick={() => setSelectedCustomer(null)}
          className="mb-4"
        >
          ← Back to All Customers
        </Button>

        {/* Customer Detail View */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl">{selectedCustomer.name}</CardTitle>
                <CardDescription>{selectedCustomer.email}</CardDescription>
              </div>
              <Badge className={`${getTierColor(selectedCustomer.loyalty_tier)} border`}>
                {selectedCustomer.loyalty_tier} - {getTierDiscount(selectedCustomer.loyalty_tier)} off
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center space-x-3 p-4 bg-zinc-900 rounded-lg">
                <DollarSign className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm text-gray-400">Total Spent</p>
                  <p className="text-xl font-bold">${selectedCustomer.total_spent.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-zinc-900 rounded-lg">
                <TrendingUp className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-400">Total Bookings</p>
                  <p className="text-xl font-bold">{selectedCustomer.total_bookings}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-zinc-900 rounded-lg">
                <Phone className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-400">Phone</p>
                  <p className="text-lg font-semibold">{selectedCustomer.phone}</p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-xl font-bold mb-4">Booking History</h3>
              <div className="space-y-3">
                {customerBookings.length === 0 ? (
                  <p className="text-gray-400">No bookings found</p>
                ) : (
                  customerBookings.map((booking) => (
                    <div key={booking.id} className="border border-zinc-700 rounded-lg p-4 bg-zinc-900">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-lg">{booking.service}</p>
                          <p className="text-sm text-gray-400">{booking.vehicleType}</p>
                          <p className="text-sm text-gray-400 mt-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {new Date(booking.preferred_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-500">${booking.total_price}</p>
                          <Badge className="mt-2">{booking.status}</Badge>
                        </div>
                      </div>
                      {booking.addons && booking.addons.length > 0 && (
                        <div className="mt-2 text-sm text-gray-400">
                          Add-ons: {booking.addons.join(', ')}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Total Customers</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total_customers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-500">${stats.total_revenue.toFixed(0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-900/10 border-amber-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-amber-300">Bronze</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.bronze_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/30 border-gray-400">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-300">Silver</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.silver_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-900/20 border-yellow-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-yellow-300">Gold</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.gold_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-900/20 border-purple-400">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-purple-300">Platinum</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.platinum_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Customer List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Customer Management</CardTitle>
              <CardDescription>View and manage customer relationships</CardDescription>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredCustomers.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No customers found</p>
            ) : (
              filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="border border-zinc-700 rounded-lg p-4 hover:bg-zinc-900 transition-colors cursor-pointer"
                  onClick={() => fetchCustomerDetails(customer.email)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{customer.name}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <Mail className="w-3 h-3" />
                          <span>{customer.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <Phone className="w-3 h-3" />
                          <span>{customer.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-400">Bookings</p>
                        <p className="text-lg font-bold">{customer.total_bookings}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-400">Spent</p>
                        <p className="text-lg font-bold text-green-500">${customer.total_spent.toFixed(0)}</p>
                      </div>
                      <Badge className={`${getTierColor(customer.loyalty_tier)} border`}>
                        <Star className="w-3 h-3 mr-1" />
                        {customer.loyalty_tier}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersPanel;
